// react
import React, { InputHTMLAttributes, useRef } from "react";
// libs
import { motion, Variants } from "framer-motion";
import { useBoolean, useMount } from "react-use";
import styled from "styled-components";
// components
import { Box } from "./Box";
import Icon from "./Icon";
// other
import { Color } from "../models/theme";
import { fill } from "../styles/utils";

interface SearchFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  iconColor?: Color | "inherit";
  onSubmit?: () => any;
}

const Wrapper = styled(motion.div)`
  position: relative;
  /* background-color: black; */
  svg {
    height: 32px;
    width: 32px;
  }
`;

const Input = styled.input`
  width: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  font-size: 1.6rem;
  line-height: 3em;
  color: inherit;
  caret-color: ${({ theme }) => theme.colors.white};
  padding-right: 32px;
  min-width: 180px;

  &::-webkit-input-placeholder {
    /* Edge */
    color: ${({ theme }) => theme.colors.white};
  }

  &:-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: ${({ theme }) => theme.colors.white};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.white};
  }
`;

const Label = styled.label`
  color: ${({ theme }) => theme.colors.white};
`;

const LabelText = styled(motion.div)`
  position: relative;
  transform-origin: left;
`;

const LabelTextPosition = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 0;
  transform-origin: left;
  transform: translateY(-50%);
  pointer-events: none;
  user-select: none;
`;

const Underline = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 2px;
  background-color: rgba(255, 255, 255, 0.6);
  overflow: hidden;
`;

const UnderlineIndicator = styled(motion.div)`
  ${fill()}
  background-color: ${({ theme }) => theme.colors.white};
`;

const IconPosition = styled(Box)`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
`;

const labelVariants: Variants = {
  focus: {
    scale: 0.6,
    x: 0,
    y: -24,
    opacity: 1,
  },
  default: {
    scale: 1,
    x: 0,
    y: 0,
    opacity: 0.8,
  },
};

const underlineVariants: Variants = {
  focus: {
    scale: 1,
  },
  default: {
    scale: 0,
  },
};

function SearchField({
  label,
  iconColor = "inherit",
  onSubmit,
  ...rest
}: SearchFieldProps) {
  const [focus, setFocus] = useBoolean(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleBlur = () => {
    if (inputRef.current) {
      if (!inputRef.current.value) {
        setFocus(false);
      }
    }
    // if
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSubmit && onSubmit();
    }
  };

  useMount(() => {
    if (inputRef.current) {
      if (!inputRef.current.value) {
        setFocus(false);
      } else {
        setFocus(true);
      }
    }
  });

  return (
    <Wrapper>
      <Label>
        <LabelTextPosition>
          <LabelText
            variants={labelVariants}
            animate={focus ? "focus" : "default"}
          >
            {label}
          </LabelText>
        </LabelTextPosition>
        <Input
          ref={inputRef}
          onFocus={() => setFocus(true)}
          onBlur={handleBlur}
          type="text"
          onKeyUp={handleKeyUp}
          {...rest}
        ></Input>
      </Label>
      <Underline>
        <UnderlineIndicator
          variants={underlineVariants}
          animate={focus ? "focus" : "default"}
        />
      </Underline>
      <IconPosition color={iconColor}>
        <Icon icon="search" />
      </IconPosition>
    </Wrapper>
  );
}

export default SearchField;
